import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

/**
 * /coach-review/:token — landing page for the pro-coach review URL.
 *
 *  Flow:
 *    1. Calls the `coach-review-redeem` edge function with the token.
 *    2. Edge function returns { hashed_token } — the OTP value for
 *       Supabase's magic-link verifyOtp flow.
 *    3. We call supabase.auth.verifyOtp() to set the session client-side.
 *    4. Navigate to /sessions/new so the coach lands directly in the
 *       planner with the pre-seeded team + sessions ready.
 *
 *  Errors land in an inline banner with a "Try again" button. The coach
 *  doesn't get a stack trace — just a plain explanation.
 */
export function CoachReview(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'redeeming' | 'verifying' | 'error'>('redeeming');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [coachName, setCoachName] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No invite token in the URL.');
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        setStatus('redeeming');
        const { data: redeem, error: redeemErr } = await supabase.functions.invoke('coach-review-redeem', {
          body: { token },
        });
        if (cancelled) return;
        if (redeemErr || !redeem) {
          // Surface the proxy detail if we can — usually 410 expired or
          // 404 not found, or 500 with a specific upstream detail.
          const ctx = (redeemErr as { context?: { json?: () => Promise<unknown> } } | null)?.context;
          let detail = redeemErr?.message ?? 'unknown error';
          try {
            if (ctx?.json) {
              const j = (await ctx.json()) as { error?: string; detail?: string };
              if (j?.error && j?.detail) detail = `${j.error} — ${j.detail}`;
              else if (j?.error) detail = j.error;
            }
          } catch { /* ignore */ }
          throw new Error(detail);
        }
        const r = redeem as { ok?: boolean; coach_name?: string; email?: string; hashed_token?: string };
        if (!r.ok || !r.hashed_token || !r.email) throw new Error('Invite redemption returned an incomplete response.');
        if (r.coach_name) setCoachName(r.coach_name);

        setStatus('verifying');
        // For magic-link redemption use `token_hash` (the hashed OTP
        // returned by admin.generateLink), NOT `token` + `email`. The
        // `token + email` shape is for the 6-digit email_otp flow.
        const { error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: r.hashed_token,
          type: 'magiclink',
        });
        if (cancelled) return;
        if (otpErr) throw new Error(`Sign-in failed: ${otpErr.message}`);

        // Land on /welcome — first-run tour for review coaches (or a
        // straight pass-through to /sessions for return visits where
        // first_run_completed_at is already set).
        navigate('/welcome', { replace: true });
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  return (
    <main style={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ maxWidth: 480, textAlign: 'center' }}>
        {status === 'redeeming' && (
          <>
            <p style={{ fontSize: 14, opacity: 0.7, margin: 0 }}>One moment…</p>
            <h1 style={{ fontSize: 24, marginTop: 8 }}>Signing you in</h1>
            <p style={{ fontSize: 16, opacity: 0.8, marginTop: 8 }}>
              {coachName ? `Setting up ${coachName}'s review session.` : 'Setting up your review session.'}
            </p>
          </>
        )}
        {status === 'verifying' && (
          <>
            <h1 style={{ fontSize: 24, marginTop: 8 }}>Almost there</h1>
            <p style={{ fontSize: 16, opacity: 0.8, marginTop: 8 }}>
              Opening the session planner…
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <h1 style={{ fontSize: 24, color: '#c0392b' }}>Couldn't open the review</h1>
            <p style={{ fontSize: 16, marginTop: 8 }}>{errorMsg ?? 'Something went wrong.'}</p>
            <p style={{ fontSize: 14, opacity: 0.7, marginTop: 16 }}>
              If your invite link has expired, ask Peter for a fresh one.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
